import { LumenProvider } from '@pipefyteam/lumen.base';
import { Button } from '@pipefyteam/lumen.components';

import { createRoot } from 'react-dom/client';

import { IframeController } from '../../helpers/IframeController';
import { getSdkUrl } from '../../utils/getSdkUrl';
import { getAgent } from './components/AIChat/api';
import { createApiService } from './components/AIChat/api/apiService';
import { Agent } from './components/AIChat/api/types/agent';
import { CHAT_EVENTS } from './constants';
import { ChatEvent, Config } from './types';

const IFRAME_ID = 'pipefy-ai-chat-iframe';

let isInitiated = false;
let iframeController: IframeController<ChatEvent> | null = null;
const bubbleHolder = document.createElement('div');

export const resetChat = () => {
  isInitiated = false;
  iframeController = null;
  bubbleHolder?.remove();
  document.getElementById(IFRAME_ID)?.remove();
};

const getIframeControllerInstance = () => {
  if (iframeController) return iframeController;
  iframeController = new IframeController<ChatEvent>({
    id: IFRAME_ID,
    src: `${getSdkUrl()}/chat`,
  });
  return iframeController;
};

export const openChat = () => {
  const iframeController = getIframeControllerInstance();
  if (iframeController.isVisible) return;
  iframeController.show();
  iframeController.sendPostMessage({
    type: CHAT_EVENTS.OPEN,
  });
};

export const closeChat = () => {
  const iframeController = getIframeControllerInstance();
  if (!iframeController.isVisible) return;
  iframeController.hide();
  document.getElementById('pipefy-ai-chat-button')?.focus();
};

export const toggleChat = () => {
  const iframeController = getIframeControllerInstance();
  iframeController.isVisible ? closeChat() : openChat();
};

export const expandChat = () => {
  const iframeController = getIframeControllerInstance();
  if (iframeController.isExpanded) return;
  iframeController.expand();
  iframeController.sendPostMessage({
    type: CHAT_EVENTS.EXPAND,
    isExpanded: true,
  });
};

export const collapseChat = () => {
  const iframeController = getIframeControllerInstance();
  if (!iframeController.isExpanded) return;
  iframeController.collapse();
  iframeController.sendPostMessage({
    type: CHAT_EVENTS.EXPAND,
    isExpanded: false,
  });
};

export const sendChatMessage = (message: string) => {
  const iframeController = getIframeControllerInstance();
  openChat();

  iframeController.sendPostMessage({
    type: CHAT_EVENTS.SEND_MESSAGE,
    message,
  });
};

const renderBubble = () => {
  bubbleHolder.style.position = 'fixed';
  bubbleHolder.style.bottom = '32px';
  bubbleHolder.style.right = '20px';
  bubbleHolder.style.zIndex = '9999';
  bubbleHolder.id = 'pipefy-ai-chat-bubble-holder';

  document.body.appendChild(bubbleHolder);
  createRoot(bubbleHolder).render(
    <LumenProvider>
      <Button
        size="md"
        onClick={openChat}
        data-testid="pipefy-ai-chat-button"
        id="pipefy-ai-chat-button"
        variant="ai"
        icon="i-ai"
      >
        AI
      </Button>
    </LumenProvider>
  );
};

export const initChat = async () => {
  if (isInitiated) {
    return;
  }

  const config: Config = {
    ...(window.PipefyAISDKSettings || {}),
    parentAppLocation: window.location.origin,
    referrer: window.location.href,
  };

  const isBuilder = config.agent === 'builder';
  let agentData: Agent | undefined;

  if (!config.pipefyToken) {
    return;
  }

  if (!isBuilder) {
    createApiService(config.pipefyToken, config.referrer);
    try {
      agentData = await getAgent({
        agentRole: config.agent,
        externalId: config.externalId,
        organizationId: config.organizationId,
      });
    } catch (e) {
      console.warn('AI Chat: Could not fetch agent', e);
      return;
    }
  }

  const iframeController = getIframeControllerInstance();

  iframeController.onEvent(CHAT_EVENTS.LOADED, async () => {
    return { ...config, agentData };
  });

  iframeController.onEvent(CHAT_EVENTS.CLOSE, async () => {
    closeChat();
  });

  iframeController.onEvent(CHAT_EVENTS.EXPAND, async ({ data }) => {
    if (data.isExpanded) {
      expandChat();
    } else {
      collapseChat();
    }
  });

  isInitiated = true;
  renderBubble();
};

window.PipefyAISDK.chat = {
  init: initChat,
  open: openChat,
  close: closeChat,
  unload: resetChat,
  toggle: toggleChat,
  get isOpen() {
    const iframeController = getIframeControllerInstance();
    return iframeController.isVisible;
  },
  expand: expandChat,
  collapse: collapseChat,
  sendMessage: sendChatMessage,
};
